import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WhitelabelService } from '@galaxy/partner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService } from '@vendasta/iam';
import { SSOService } from '@vendasta/sso';
import { Observable, firstValueFrom, map, switchMap, take } from 'rxjs';
import { devServer, partnerId, environment } from '../../globals';
import { AccountGroup } from '../account-group';
import { AccountGroupService } from '../account-group/account-group.service';
import { AuthService } from '../auth.service';
import { ImageService } from '../core/image.service';
import { SalesContact, SalesContactService } from '../sales-contact';
import { fakeHasCustomIDPForVBC } from './auth-wall.service';
import { SessionService } from '@galaxy/core';

const CREATE_PROFILE_URL = '/internalApi/v3/user/previewModeClaimUser/';

export interface AuthWallDialogData {
  creatingProfile: boolean;
}

@Component({
  templateUrl: './auth-wall-dialog.component.html',
  styleUrls: ['./auth-wall-dialog.component.scss'],
  imports: [
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    TranslateModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    FormsModule,
  ],
})
export class AuthWallDialogComponent implements OnInit {
  @ViewChild('logInForm') loginForm: NgForm;
  @ViewChild('createProfileForm') creadteProfileForm: NgForm;
  formSubmitted = false;
  creatingProfile = false;
  hasError = false;
  hidePasswordLetters = true;
  googleLogoSrc: string;
  loading = false;
  salesPerson$: Observable<SalesContact>;
  account$: Observable<AccountGroup>;
  whitelabelBusinessCenterName$: Observable<string>;
  associatedAccountGroupIds$: Observable<string[]>;
  partnerHasCustomLogin$: Observable<boolean>;
  header$: Observable<string>;
  errorMessage$: Observable<string>;
  emailAddressSignUp: string;
  emailAddressLogin = '';
  passwordLogin = '';

  // local story
  sessionToken: string;
  environment = environment;
  isLocalStory = devServer;

  constructor(
    public dialogRef: MatDialogRef<AuthWallDialogComponent>,
    private http: HttpClient,
    private accountGroupService: AccountGroupService,
    private salesContactService: SalesContactService,
    private whiteLabelService: WhitelabelService,
    private imageService: ImageService,
    private authService: AuthService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private ssoService: SSOService,
    @Inject(MAT_DIALOG_DATA) public data: AuthWallDialogData,
    private iamService: IAMService,
    private sessionService: SessionService,
  ) {}

  private apiOptions(): { headers: HttpHeaders; withCredentials: boolean } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      withCredentials: true,
    };
  }

  ngOnInit(): void {
    this.creatingProfile = this.data.creatingProfile;
    this.googleLogoSrc = this.imageService.getImageSrc('google-logo.jpg');
    firstValueFrom(this.authService.temporarySubjectEmail$).then((email) => (this.emailAddressSignUp = email));
    this.account$ = this.accountGroupService.currentAccountGroup$;

    this.whitelabelBusinessCenterName$ = this.whiteLabelService.getBranding(partnerId).pipe(
      map((branding) => {
        return branding.apps.VBC.name;
      }),
    );

    this.errorMessage$ = this.salesContactService.salesContact$.pipe(
      switchMap((salesPersonContact) =>
        salesPersonContact && salesPersonContact?.email
          ? this.translate.get('AUTH_WALL_MODAL.CREATE_PROFILEERROR', { salesperson_email: salesPersonContact.email })
          : this.translate.get('AUTH_WALL_MODAL.CREATE_PROFILE.ERROR_NO_SALESPERSON'),
      ),
    );

    this.header$ = this.account$.pipe(
      switchMap((account) =>
        account
          ? this.translate.get('AUTH_WALL_MODAL.HEADER', { business_name: account.companyName })
          : this.translate.get('AUTH_WALL_MODAL.HEADER_NO_ACCOUNT'),
      ),
    );

    this.partnerHasCustomLogin$ = fakeHasCustomIDPForVBC(partnerId); // TODO: call SSO's HasCustomerIdentityProvider
  }

  setCreatingProfileFalse(): void {
    this.creatingProfile = false;
    this.formSubmitted = false;
    this.hasError = false;
  }

  setCreatingProfileTrue(): void {
    this.creatingProfile = true;
  }

  submitCreateProfileForm(): void {
    if (!this.creadteProfileForm.valid) {
      return;
    }
    this.createProfile().subscribe({
      next: () => {
        this.formSubmitted = true;
        this.hasError = false;
      },
      error: () => {
        this.formSubmitted = true;
        this.hasError = true;
      },
    });
  }

  loginToVBCThroughSSO(): void {
    this.loading = true;
    this.account$.pipe(take(1)).subscribe((account: AccountGroup) => {
      const url = this.ssoService.getServiceGatewayURLForAccount(
        'VBC',
        account.accountGroupId,
        window.location.pathname,
      );
      window.location.href = url;
    });
  }

  async login(): Promise<void> {
    this.loading = true;

    if (devServer && this.sessionToken) {
      const sess = this.sessionToken;
      this.sessionService.setSessionId(sess);
      try {
        await firstValueFrom(this.authService.createVBCSession(sess, partnerId));
        this.closeDialog();
        window.location.reload();
      } catch (err) {
        this.snackbarService.openErrorSnack(`Invalid login: ${err.message}`);
      } finally {
        this.loading = false;
      }
      return;
    }

    if (this.loginForm.invalid) {
      return;
    }

    try {
      await this.authService.login(this.emailAddressLogin, this.passwordLogin);
      this.closeDialog();
      window.location.reload();
    } catch (err) {
      this.snackbarService.openErrorSnack('AUTH_WALL_MODAL.LOGIN.INVALID_LOGIN');
    } finally {
      this.loading = false;
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  private createProfile(): Observable<any> {
    return this.account$.pipe(
      take(1),
      switchMap((account) => {
        return this.http.post(
          CREATE_PROFILE_URL,
          {
            partnerId: partnerId,
            accountGroupId: account.accountGroupId,
            email: this.emailAddressSignUp,
          },
          this.apiOptions(),
        );
      }),
    );
  }

  googleLogin(): void {
    if (devServer) {
      this.iamService.ssoLogin(window.location.href);
    } else {
      window.location.href = '/redirect/sso-login/?provider=google&nextUrl=' + window.location.href;
    }
  }
}

export const showAuthDialog = () => {
  const dialog = inject(MatDialog);
  return (showSignUp = false) => {
    return dialog.open(AuthWallDialogComponent, {
      width: '450px',
      data: { creatingProfile: showSignUp },
    });
  };
};
